import { createTheme } from '@mui/material/styles';

// ─── Paleta central ──────────────────────────────────────────────────────────
// Fonte única de verdade para todas as cores da aplicação.
// Nenhum componente deve declarar valores hex/rgb próprios: importe `COLORS`.
export const COLORS = {
  // Neutros
  white: '#ffffff',
  black: '#000000',
  offWhite: '#F5F5F5',
  elevated: '#f8f9fa',
  gray100: '#EAEAEA',
  gray200: '#e0e0e0',
  gray300: '#ddd',
  gray400: '#b3b3b3',
  gray500: '#b0bec5',
  gray600: '#C5C0B8',
  gray700: '#8A857E',
  gray800: '#888888',
  gray900: '#757575',
  textDark: '#212121',
  brandGray: '#4D4D4F',
  brandDark: '#222224',

  // Marca (laranja)
  orange: '#eb5b25',
  orangeVivid: '#F3470C',
  orangeDeep: '#C8602A',
  orangeSoft: '#f4a380',
  orangeLight: '#ff8f6b',
  orangeLighter: '#ffa07d',
  orangeLightest: '#ffb38f',
  orangeCoral: '#FF7A4C',

  // Landing page
  cream: '#F2EEE7',
  creamSoft: '#E9E3D8',
  beige: '#d8d0c4',
  dark: '#1C1B19',
  darkSoft: '#2A2825',
  line: 'rgba(28,27,25,0.12)',

  // Login / Cadastro público
  authCream: '#EEEAE3',
  authGraphite: '#1C1C1C',

  // Fundos escuros
  darkBg: '#121212',
  darkDeep: '#1a1a1a',
  darkPaper: '#1e1e1e',
  darkElevated: '#2c2c2c',

  // Divisores
  dividerLight: 'rgba(0, 0, 0, 0.12)',
  dividerDark: 'rgba(255, 255, 255, 0.12)',

  // Status
  success: '#66BB6A',
  successDark: '#4CAF50',
  error: '#EF5350',
  errorDark: '#F44336',
  errorDeep: '#D32F2F',
  errorLight: '#E57373',
  warning: '#FFA726',
  warningDark: '#FF9800',
  info: '#1E88E5',
  infoDark: '#2196F3',
  infoSoft: '#E3F2FD',
  statusOpenBg: '#FFF3E0',
  statusDoneBg: '#E8F5E9',
  statusCancelledBg: '#FFEBEE',
  alert: '#ffb74d',
  danger: '#7a0012',
  pink: '#E91E63',
  purple: '#9C27B0',
  purpleSoft: '#7e57c2',
  purpleVivid: '#7C3AED',
  chartPurple: '#8E24AA',
  cyan: '#00BCD4',
  indigo: '#3F51B5',
  redLight: '#FF5252',
  brown: '#795548',
  blueGray: '#607D8B',

  // Redes sociais
  whatsapp: '#25D366',
  whatsappDark: '#128C7E',
  instagramPink: '#E1306C',
  instagramPurple: '#833ab4',
  instagramPurpleSoft: '#833ab410',
  instagramPurpleDark: '#6f2fa0',
  instagramRed: '#fd1d1d',
  instagramRedSoft: '#fd1d1d08',
  instagramRedDark: '#d41919',
  instagramYellow: '#fcb045',
  instagramYellowSoft: '#fcb04510',
  instagramYellowDark: '#e0a03b',

  // Fundos IA / Instagram
  aiNavy: '#1a1a2e',
  aiNavySoft: '#16213e',
  aiBlue: '#0f3460',
  aiPurple: '#533483',

  // Dashboards
  dashboardPj: '#5B9BD5',
  dashboardPf: '#E07B39',
  blueDrive: '#2882FB',

  // Superfícies de marca (tints e bordas translúcidas)
  orangeTint: 'rgba(235,91,37,0.08)',
  orangeTintSoft: 'rgba(235,91,37,0.04)',
  orangeTintStrong: 'rgba(235,91,37,0.16)',
  orangeBorder: 'rgba(235,91,37,0.35)',
  orangeBorderSoft: 'rgba(235,91,37,0.22)',
  creamTint: 'rgba(242,238,231,0.72)',
  creamLine: 'rgba(242,238,231,0.16)',
  overlayDark: 'rgba(28,27,25,0.72)',
  overlayDarker: 'rgba(12,11,10,0.94)',
};

// ─── Tokens de interface de dados ────────────────────────────────────────────
// Escalas e papéis semânticos das telas de gestão (dashboard, relatórios).
// Nenhum componente declara px/hex solto: importa daqui. Os tons de status
// abaixo são de TEXTO e passam WCAG AA (>= 4.5:1) sobre superfície branca —
// os `COLORS.success`/`warning` acima são tons de preenchimento e reprovam
// contraste quando usados em texto (ex.: #4CAF50 = 2.77:1).
// Escala de espaçamento de referência (px). No `sx` usamos `theme.spacing`
// (base 8), então só estas paradas são permitidas: 8 · 12 · 16 · 24 · 32 · 40.
// 4px aparece apenas como ajuste óptico (gap de ícone/legenda).
export const SPACE = {
  '3xs': 4,
  '2xs': 8,
  xs: 12,
  sm: 16,
  md: 24,
  lg: 32,
  xl: 48,
};

export const RADIUS = {
  input: 4,
  panel: 8,
  pill: 999,
};

// Escada de opacidade das linhas. A ordem é invariante (e testada): a grade de
// fundo é cenário, a barra é marca de dado, o fio é estrutura e o cabeçalho de
// tabela é a linha mais forte. Antes os três primeiros degraus estavam em
// 6% / 8% / 10% e se misturavam na tela.
export const LINE_ALPHA = {
  grid: 0.06,
  track: 0.1,
  hairline: 0.15,
  strong: 0.22,
  /**
   * Régua de seção (abertura de capítulo do dashboard). É o degrau mais forte
   * da escada: separa blocos, não linhas de tabela. Sem ele, o dashboard tem
   * seis fios iguais e nenhuma hierarquia de leitura.
   */
  rule: 0.28,
};

const inkWithAlpha = (alpha) => `rgba(28,27,25,${alpha})`;

export const BORDER = {
  hairline: `1px solid ${inkWithAlpha(LINE_ALPHA.hairline)}`,
  strong: `1px solid ${inkWithAlpha(LINE_ALPHA.strong)}`,
  rule: `1px solid ${inkWithAlpha(LINE_ALPHA.rule)}`,
  focus: '2px solid #eb5b25',
};

export const SURFACE = {
  canvas: COLORS.cream,
  panel: COLORS.white,
  subtle: '#FAF8F5',
  hover: 'rgba(28,27,25,0.03)',
  /** Capa do dashboard: grafite da marca. */
  cover: COLORS.dark,
  coverDark: COLORS.darkSoft,
};

/**
 * Tinta sobre a superfície escura (capa).
 *
 * O creme da marca em três pesos: texto, apoio e legenda. Todos acima de
 * 4.5:1 sobre `SURFACE.cover` — a capa não é um lugar para texto apagado.
 */
export const ON_COVER = {
  ink: COLORS.cream,
  soft: 'rgba(242,238,231,0.72)',
  muted: 'rgba(242,238,231,0.58)',
  line: 'rgba(242,238,231,0.16)',
};

// Cor com significado: positivo/negativo/atenção/informação. A marca não entra
// aqui de propósito — laranja é interação (seleção, foco, link, ação).
export const SEMANTIC = {
  ink: COLORS.dark,
  // Metadados ainda precisam de contraste AA (4.5:1) sobre superfície branca:
  // 0.72 ≈ 6.3:1 e 0.62 ≈ 4.8:1. Abaixo disso o texto "recua" demais e deixa
  // de ser legível — recuo é hierarquia, não invisibilidade.
  inkSoft: 'rgba(28,27,25,0.72)',
  inkMuted: 'rgba(28,27,25,0.62)',
  positive: '#1E7A46',
  negative: '#B42318',
  warning: '#B54708',
  info: '#1B5FA8',
  positiveBg: 'rgba(30,122,70,0.08)',
  negativeBg: 'rgba(180,35,24,0.08)',
  warningBg: 'rgba(181,71,8,0.08)',
  barFill: 'rgba(28,27,25,0.72)',
  barFillSoft: 'rgba(28,27,25,0.30)',
  track: inkWithAlpha(LINE_ALPHA.track),
  /**
   * ── Acento de marca ("agora") ─────────────────────────────────────────────
   * O laranja NÃO identifica série nem decora KPI: ele marca o ponto de
   * leitura — o mês atual no gráfico, o "você está aqui" da tendência, o
   * filete da rubrica. Histórico realizado continua grafite; estado continua
   * verde/âmbar/vermelho. Um acento por gráfico, nunca dois.
   */
  accent: COLORS.orange,
  accentTint: COLORS.orangeTintStrong,
  accentLine: COLORS.orangeBorder,
  /**
   * Hachura diagonal: convenção de impressão para "ainda não realizado".
   * Substitui o tom claro chapado em "a receber" sem inventar uma cor nova.
   */
  hatch: 'repeating-linear-gradient(135deg, rgba(28,27,25,0.42) 0 1px, transparent 1px 5px)',
  hatchBg: 'rgba(28,27,25,0.10)',
  /**
   * Linha de referência (média do período) desenhada ATRÁS das barras: aparece
   * nos vãos, nunca por cima do dado. Precisa ser mais forte que a grade e que o
   * fio — a 10% ela sumia na tela.
   */
  guide: 'rgba(28,27,25,0.35)',
};

// Escala tipográfica: só três pesos (400/500/600). Valores são o que salta;
// rótulos recuam. `NUM` garante alinhamento de dígitos em qualquer número.
export const TYPE = {
  /**
   * Display da capa: Poppins 900 é a voz da marca no resto do app (PageHeader,
   * sidebar, títulos do cliente). O dashboard era a única tela fora dessa
   * família tipográfica — e é justamente a primeira que se vê.
   */
  display: {
    fontFamily: '"Poppins", sans-serif',
    fontWeight: 900,
    lineHeight: 1.06,
    letterSpacing: '-0.02em',
  },
  pageSubtitle: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
  eyebrow: {
    fontFamily: '"Poppins", sans-serif',
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
  },
  sectionTitle: {
    fontFamily: '"Poppins", sans-serif',
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  ordinal: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '0.06em',
  },
  kpiLabel: { fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.01em' },
  kpiPrimary: {
    fontSize: { xs: '1.875rem', md: '2.25rem' },
    fontWeight: 600,
    lineHeight: 1.05,
    letterSpacing: '-0.02em',
  },
  kpiSecondary: {
    fontSize: '1.25rem',
    fontWeight: 600,
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
  },
  metric: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 },
  body: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
  caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
  micro: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
};

export const NUM = { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1' };

/** Numeral de destaque: tabular (alinha em coluna) + tracking negativo (óptico). */
export const STAT_NUM = { ...NUM, letterSpacing: '-0.015em' };

export const A11Y = {
  // Visível apenas para leitores de tela (não usar `display: none`).
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
};

// ─── Largura das páginas internas (admin + área do cliente) ──────────────────
// Antes cada tela decidia a própria largura (Dashboard 1440, Notificações 1120,
// painel do cliente 1200, Configurações 760): o mesmo painel tinha quatro
// "papéis" diferentes na tela. Agora existe um padrão único — a página ocupa
// toda a área útil ao lado da sidebar, como em Clientes.
//
// Para estreitar (ou centralizar) todas as telas de uma vez, basta trocar
// `PAGE.maxWidth` por um número: nenhuma página precisa ser tocada.
export const PAGE = {
  /** Largura padrão do conteúdo de qualquer página interna. */
  maxWidth: '100%',
  /** Respiro lateral entre a sidebar e o conteúdo (usado pelos layouts). */
  paddingX: { xs: 2, sm: 2.5, md: 3 },
  /** Respiro vertical do topo/rodapé do conteúdo (usado pelos layouts). */
  paddingY: { xs: 2, sm: 2.5, md: 3 },
};

export const DASHBOARD = {
  /** Herda o padrão único de largura de página (antes: 1440, que estreitava). */
  maxWidth: PAGE.maxWidth,
  panelPadding: { xs: 2, md: 3 },
  sectionGap: { xs: 4, md: 5 },
  /**
   * Altura do placeholder da capa no skeleton. A capa é o `PageHeader`
   * compartilhado (padding 3/4.5 + rubrica + título + subtítulo), não mais a
   * faixa fina que o dashboard tinha quando tinha hero próprio.
   */
  coverMinHeight: { xs: 236, md: 268 },
};

// ─── Gradientes centralizados ────────────────────────────────────────────────
export const GRADIENTS = {
  primary: `linear-gradient(135deg, ${COLORS.orange} 0%, ${COLORS.orangeLight} 100%)`,
  secondary: `linear-gradient(135deg, ${COLORS.orangeLight} 0%, ${COLORS.orangeLighter} 100%)`,
  tertiary: `linear-gradient(135deg, ${COLORS.orangeLighter} 0%, ${COLORS.orangeLightest} 100%)`,
  hero: `linear-gradient(45deg, ${COLORS.orange} 30%, ${COLORS.orangeLight} 90%)`,
  darkBg: `linear-gradient(135deg, ${COLORS.darkBg} 0%, ${COLORS.darkDeep} 100%)`,
  darkSection: `linear-gradient(180deg, ${COLORS.darkBg} 0%, ${COLORS.darkDeep} 100%)`,
  instagram: `linear-gradient(135deg, ${COLORS.instagramPurple}, ${COLORS.instagramRed}, ${COLORS.instagramYellow})`,
  instagramTwo: `linear-gradient(135deg, ${COLORS.instagramPurple}, ${COLORS.instagramRed})`,
  instagramSoft: `linear-gradient(135deg, ${COLORS.instagramPurpleSoft}, ${COLORS.instagramRedSoft}, ${COLORS.instagramYellowSoft})`,
  instagramHover: `linear-gradient(135deg, ${COLORS.instagramPurpleDark}, ${COLORS.instagramRedDark})`,
  instagramHoverFull: `linear-gradient(135deg, ${COLORS.instagramPurpleDark}, ${COLORS.instagramRedDark}, ${COLORS.instagramYellowDark})`,
  instagramDark: `linear-gradient(135deg, ${COLORS.aiNavy}, ${COLORS.aiNavySoft})`,
  instagramButton: `linear-gradient(135deg, ${COLORS.aiNavy}, ${COLORS.aiBlue})`,
  instagramButtonHover: `linear-gradient(135deg, ${COLORS.aiBlue}, ${COLORS.aiPurple})`,
  orcamentoDark: `linear-gradient(135deg, ${COLORS.darkBg} 0%, ${COLORS.darkDeep} 100%)`,
  clubDark: `linear-gradient(180deg, ${COLORS.darkBg} 0%, ${COLORS.darkDeep} 50%, ${COLORS.darkBg} 100%)`,
  clubLight: `linear-gradient(180deg, ${COLORS.offWhite} 0%, ${COLORS.white} 50%, ${COLORS.offWhite} 100%)`,
};

// ─── Série de cores para gráficos da área do cliente ─────────────────────────
export const CHART_COLORS = [
  COLORS.orangeVivid,
  COLORS.orangeCoral,
  COLORS.brandDark,
  COLORS.brandGray,
  COLORS.gray100,
];

// ─── Tokens de interface mobile ──────────────────────────────────────────────
// Medidas de toque e áreas seguras em um só lugar. `env(safe-area-inset-*)`
// só existe em CSS, então os componentes montam o `calc()` a partir daqui em
// vez de repetir a string em cada tela (e esquecer em alguma).
export const MOBILE = {
  /** Alvo de toque mínimo recomendado (Apple HIG / Material). */
  touchTarget: 44,
  /** Movimento mínimo (px) para reconhecer um gesto. */
  gestureTolerance: 10,
  /** Fração do overlay que precisa ser arrastada para fechar. */
  dismissRatio: 0.32,
  safeAreaTop: 'env(safe-area-inset-top, 0px)',
  safeAreaBottom: 'env(safe-area-inset-bottom, 0px)',
  safeAreaLeft: 'env(safe-area-inset-left, 0px)',
  safeAreaRight: 'env(safe-area-inset-right, 0px)',
  /**
   * Altura útil do header fixo do cliente no mobile (logo + avatar + sino +
   * menu "⋯"), já com respiro. O conteúdo (`UserLayoutBrand`) e os elementos
   * fixos que ficam logo abaixo dele (`UserOrders`) montam o `calc()` a partir
   * daqui em vez de repetir 112/118px — que era a altura de quando existia a
   * faixa de pílulas duplicada.
   */
  userHeaderHeight: 64,
  /**
   * Barra de navegação inferior do cliente. Fica ABAIXO de qualquer
   * `Dialog`/`Modal` (`theme.zIndex.modal` = 1300), senão ela aparece por cima
   * das telas cheias do mobile (prévia de arquivo, detalhe do pedido).
   */
  bottomNavZIndex: 1299,
  /** Barra de ações fixa no rodapé (mobile). */
  actionBarSx: {
    position: 'sticky',
    bottom: 0,
    mt: 'auto',
    zIndex: 7,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 0.8,
    pt: 1,
    paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
    bgcolor: 'background.default',
  },
};

const getDesignTokens = (mode) => ({
  shape: {
    borderRadius: 8,
  },
  // ─── Densidade das tabelas ────────────────────────────────────────────────
  // Layout em px porque as listagens usam grade fixa (`tableLayout: fixed`):
  // as colunas não mudam de largura conforme o conteúdo. Cores continuam
  // vindo exclusivamente da palette (light/dark).
  table: {
    cellPaddingX: 12,
    headerHeight: 38,
    actionGap: 2,
    quickLinkWidth: 57,
    quickLinkHeight: 24,
    densities: {
      compact: { cellPaddingY: 2, actionSlot: 26, actionIcon: 18, textButton: 54 },
      standard: { cellPaddingY: 6, actionSlot: 30, actionIcon: 20, textButton: 60 },
    },
  },
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light mode
          primary: { main: COLORS.orange }, // Laranja
          secondary: { main: COLORS.brandDark }, // Cinza escuro
          background: {
            default: COLORS.offWhite,
            paper: COLORS.white,
            elevated: COLORS.elevated,
          },
          text: {
            primary: COLORS.textDark,
            secondary: COLORS.gray900,
          },
          divider: COLORS.dividerLight,
        }
      : {
          // Dark mode
          primary: { main: COLORS.orange }, // Laranja mais vibrante para dark mode
          secondary: { main: COLORS.gray500 },
          background: {
            default: COLORS.darkBg,
            paper: COLORS.darkPaper,
            elevated: COLORS.darkElevated,
          },
          text: {
            primary: COLORS.white,
            secondary: COLORS.gray400,
          },
          divider: COLORS.dividerDark,
        }),
    green: { main: COLORS.successDark },
    blueDrive: { main: COLORS.blueDrive },
    error: { main: COLORS.errorLight },
    alert: { main: COLORS.alert },
    danger: { main: COLORS.danger },
    // Status colors
    status: {
      aberto: { main: COLORS.warning, bg: COLORS.statusOpenBg },
      emAndamento: { main: COLORS.info, bg: COLORS.infoSoft },
      concluido: { main: COLORS.success, bg: COLORS.statusDoneBg },
      cancelado: { main: COLORS.error, bg: COLORS.statusCancelledBg },
      pendente: { main: COLORS.warning },
      pago: { main: COLORS.success },
      pagoComSaldo: { main: COLORS.success },
      pagoParcialSaldo: { main: COLORS.info },
    },
    // Chart colors
    chart: {
      primary: COLORS.successDark,
      secondary: COLORS.infoDark,
      tertiary: COLORS.chartPurple,
      quaternary: COLORS.errorDeep,
      quinary: COLORS.warningDark,
      senary: COLORS.cyan,
      septenary: COLORS.purple,
      octonary: COLORS.pink,
      nonary: COLORS.indigo,
      denary: COLORS.redLight,
      undenary: COLORS.brown,
      duodenary: COLORS.blueGray,
    },
    // Gradient colors
    gradients: GRADIENTS,
    // WhatsApp color
    whatsapp: { main: COLORS.whatsapp, hover: COLORS.whatsappDark },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'light' ? COLORS.white : COLORS.darkPaper,
          borderRadius: 8,
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          transition: 'box-shadow 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0px 4px 8px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
          marginTop: '0px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: '0px',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: '0px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

const lightTheme = createTheme(getDesignTokens('light'));
const darkTheme = createTheme(getDesignTokens('dark'));

export { darkTheme, lightTheme };
